import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { Ruler } from "lucide-react";
import { useCalculatorStore } from "@/store/calculatorStore";
import { computeMPBR, CONVERT } from "@/utils/ballisticsMath";
import { useT } from "@/i18n/translations";
import { Slider } from "@/components/ui/slider";

const Stat = ({ label, value, unit, accent, testId }) => (
  <div className="rounded border border-dase-border bg-dase-input px-3 py-2" data-testid={testId}>
    <div className="text-[9px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="mt-0.5 font-mono text-lg font-black">
      <span className={accent ? "text-dase-red" : "text-zinc-100"}>{value}</span>
      <span className="ml-1 text-[10px] font-bold text-zinc-500">{unit}</span>
    </div>
  </div>
);

export const MpbrPanel = () => {
  const t = useT();
  const imperial = useCalculatorStore((s) => s.unitSystem) === "imperial";
  const vitalWindow = useCalculatorStore((s) => s.vitalWindow);
  const setField = useCalculatorStore((s) => s.setField);

  const inp = useCalculatorStore(
    useShallow((s) => ({
      v0: s.v0,
      bc: s.bc,
      sightHeight: s.sightHeight,
      tempC: s.tempC,
      altitude: s.altitude,
      pressureHpa: s.pressureHpa,
    }))
  );

  const mpbr = useMemo(() => computeMPBR(inp, vitalWindow), [inp, vitalWindow]);

  const distU = imperial ? "yd" : "m";
  const d = (m) => (m == null ? "–" : (imperial ? CONVERT.mToYd(m) : m).toFixed(0));
  const ord = imperial ? CONVERT.mToIn(mpbr.maxOrdinateM) : mpbr.maxOrdinateM * 100;
  const ordU = imperial ? "in" : "cm";

  // Vital window slider in cm/in
  const winDisp = imperial ? CONVERT.mToIn(vitalWindow) : vitalWindow * 100;
  const winU = imperial ? "in" : "cm";

  return (
    <div className="rounded-md border border-dase-border bg-dase-panel p-4" data-testid="mpbr-panel">
      <div className="mb-4 flex items-center gap-2 border-b border-dase-border pb-2">
        <Ruler className="h-4 w-4 text-dase-red" strokeWidth={2.2} />
        <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-200">{t("mpbr.title")}</h2>
      </div>

      <div className="mb-4">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
            {t("mpbr.vitalWindow")}
          </span>
          <span className="font-mono text-sm font-bold text-zinc-100">
            {winDisp.toFixed(imperial ? 2 : 1)}
            <span className="ml-1 text-[10px] font-bold text-dase-red">{winU}</span>
          </span>
        </div>
        <Slider
          value={[Number(winDisp.toFixed(2))]}
          min={imperial ? 0.2 : 0.5}
          max={imperial ? 6 : 15}
          step={imperial ? 0.1 : 0.5}
          onValueChange={(v) => setField("vitalWindow", imperial ? CONVERT.inToM(v[0]) : v[0] / 100)}
          data-testid="mpbr-vital-window"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Stat label={t("mpbr.nearZero")} value={d(mpbr.nearZeroM)} unit={distU} testId="mpbr-near-zero" />
        <Stat label={t("mpbr.farZero")} value={d(mpbr.farZeroM)} unit={distU} testId="mpbr-far-zero" />
        <Stat label={t("mpbr.mpbr")} value={d(mpbr.mpbrM)} unit={distU} accent testId="mpbr-max" />
        <Stat label={t("mpbr.maxOrd")} value={ord.toFixed(1)} unit={ordU} testId="mpbr-max-ord" />
      </div>
      <p className="mt-3 text-[10px] leading-relaxed text-zinc-500">{t("mpbr.hint")}</p>
    </div>
  );
};

export default MpbrPanel;
